import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { createOffer } from "../../../../services/pos/Tables";
import { getAllProducts } from "../../../../services/menuPage";


const CreateOfferForm = ({ onSuccess }) => {
  const [offer, setOffer] = useState({
    name: "",
    name_tag: "",
    description: "",
    start_date: "",
    end_date: "",
    discount: 0,
    total_price: "",
    items_number: "",
    // each item now has a category field to choose group first
    items: [{ category: "", product_id: "", price: "", quantity: "", note: "" }],
  });

  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({}); // validation errors

  // Arabic labels for the main fields
  const labelsMap = {
    name: "الاسم",
    name_tag: "التاج",
    description: "الوصف",
    start_date: "تاريخ البداية",
    end_date: "تاريخ النهاية",
    discount: "قيمة الخصم",
    total_price: "السعر الإجمالي",
    items_number: "عدد المنتجات",
  };

  // derive unique categories from products (handles array or res.data)
  const categories = useMemo(() => {
    const list = Array.isArray(products)
      ? products
      : Array.isArray(products?.data)
      ? products.data
      : [];
    const s = new Set();
    list.forEach((p) => {
      if (p?.category_name) s.add(p.category_name);
    });
    return Array.from(s);
  }, [products]);

  // حساب المجاميع من العناصر: مجموع الأسعار (مجموع قيمة كل سعر منفرد)، إجمالي الكميات، وإجمالي المبلغ (سعر * كمية)
  const itemsTotals = useMemo(() => {
    let sumPrices = 0;
    let totalQuantity = 0;
    let totalAmount = 0;
    (offer.items || []).forEach((it) => {
      const price = parseFloat(it.price) || 0;
      const qty = parseInt(it.quantity) || 0;
      sumPrices += price;
      totalQuantity += qty;
      totalAmount += price * qty;
    });
    return { sumPrices, totalQuantity, totalAmount };
  }, [offer.items]);

  // عندما يتغير إجمالي المبلغ، نزامن حقل السعر الإجمالي تلقائياً
  useEffect(() => {
    const amt = itemsTotals.totalAmount || 0;
    // نعرضه كقيمة ذات منزلتين عشريتين (يمكن تعديل التنسيق حسب الحاجة)
    setOffer((prev) => ({ ...prev, total_price: amt ? amt.toFixed(2) : "" }));
  }, [itemsTotals.totalAmount]);

  // مزامنة عدد المنتجات مع طول مصفوفة العناصر
  useEffect(() => {
    setOffer((prev) => ({ ...prev, items_number: String((prev.items || []).length) }));
  }, [offer.items]);

  useEffect(() => {
    getAllProducts()
      .then((res) => setProducts(res))
      .catch((err) => console.error("❌ فشل في تحميل المنتجات:", err.message));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOffer((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...offer.items];
    // if changing category, reset product selection for that item
    if (field === "category") {
      updatedItems[index].category = value;
      updatedItems[index].product_id = "";
      // keep price as-is or clear if desired:
      // updatedItems[index].price = "";
    } else if (field === "product_id") {
      updatedItems[index].product_id = value;
      // find selected product in loaded products (handle both array or res.data)
      const list = Array.isArray(products) ? products : Array.isArray(products?.data) ? products.data : [];
      const selected = list.find((p) => String(p.id) === String(value));
      if (selected) {
        // set default price from product (allow user to edit later)
        updatedItems[index].price = selected.price !== undefined ? String(selected.price) : (selected.product_cost !== undefined ? String(selected.product_cost) : updatedItems[index].price || "");
        // optionally set category to product's category if you want:
        // updatedItems[index].category = selected.category_name || updatedItems[index].category;
        // clear related item errors
        setErrors((prev) => {
          if (!prev.items) return prev;
          const newItemsErr = [...prev.items];
          newItemsErr[index] = { ...(newItemsErr[index] || {}) };
          delete newItemsErr[index].product_id;
          delete newItemsErr[index].price;
          const np = { ...prev, items: newItemsErr };
          if (newItemsErr.every((it) => Object.keys(it || {}).length === 0)) delete np.items;
          return np;
        });
      }
    } else {
      updatedItems[index][field] = value;
    }
    setOffer((prev) => ({ ...prev, items: updatedItems }));
  };
  
  // remove item at index and sync errors
  const removeItem = (index) => {
    setOffer((prev) => {
      const items = prev.items.filter((_, i) => i !== index);
      return { ...prev, items };
    });
    setErrors((prev) => {
      if (!prev.items) return prev;
      const newItemsErr = prev.items.filter((_, i) => i !== index);
      const np = { ...prev, items: newItemsErr };
      // remove items key if empty
      if (newItemsErr.length === 0) delete np.items;
      return np;
    });
  };

  // validate offer before submit
  const validateOffer = () => {
    const e = {};
    if (!offer.name || !offer.name.trim()) e.name = "الاسم مطلوب";
    if (!offer.name_tag || !offer.name_tag.trim()) e.name_tag = "التاج مطلوب";
    if (!offer.start_date) e.start_date = "حدد تاريخ البداية";
    if (!offer.end_date) e.end_date = "حدد تاريخ النهاية";
    if (offer.start_date && offer.end_date && new Date(offer.start_date) > new Date(offer.end_date)) {
      e.end_date = "تاريخ النهاية يجب أن يكون بعد البداية";
    }
    if (offer.discount === "" || offer.discount === null) e.discount = "حدد قيمة الخصم";
    else if (isNaN(Number(offer.discount))) e.discount = "قيمة الخصم غير صحيحة";
    if (offer.total_price === "" || offer.total_price === null) e.total_price = "حدد السعر الإجمالي";
    else if (isNaN(Number(offer.total_price))) e.total_price = "قيمة السعر الإجمالي غير صحيحة";
    if (!offer.items_number) e.items_number = "عدد المنتجات مطلوب";
    else if (isNaN(Number(offer.items_number))) e.items_number = "عدد المنتجات غير صحيح";

    const itemsErr = [];
    offer.items.forEach((it, idx) => {
      const ie = {};
      if (!it.category) ie.category = "اختر المجموعة";
      if (!it.product_id) ie.product_id = "اختر المنتج";
      if (it.price === "" || it.price === null) ie.price = "حدد السعر";
      else if (isNaN(Number(it.price)) || Number(it.price) <= 0) ie.price = "السعر يجب أن يكون رقمًا > 0";
      if (it.quantity === "" || it.quantity === null) ie.quantity = "حدد الكمية";
      else if (isNaN(Number(it.quantity)) || Number(it.quantity) <= 0) ie.quantity = "الكمية يجب أن تكون رقمًا > 0";
      itemsErr[idx] = ie;
    });
    if (itemsErr.some((obj) => Object.keys(obj).length > 0)) e.items = itemsErr;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const addItem = () => {
    setOffer((prev) => ({
      ...prev,
      items: [...prev.items, { category: "", product_id: "", price: "", quantity: "", note: "" }],
    }));
  };

  const handleSubmit = async () => {
    if (!validateOffer()) {
      alert("❌ يوجد أخطاء في نموذج الإدخال، تحقق من الحقول المظللة.");
      return;
    }
    try {
      const formattedOffer = {
        ...offer,
        name_tage: offer.name_tag, // spelling per API
        discount: parseFloat(offer.discount),
        total_price: parseFloat(offer.total_price),
        items_number: parseInt(offer.items_number),
        itmes: offer.items.map((item) => ({
          product_id: parseInt(item.product_id),
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          note: item.note || "",
        })),
      };

      delete formattedOffer.items;

      const res = await createOffer(formattedOffer);
      alert("✅ تم إنشاء العرض بنجاح");
      if (onSuccess) onSuccess(); // إشعار لتحديث القائمة
    } catch (err) {
      alert("❌ فشل في إنشاء العرض: " + err.message);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>إضافة عرض جديد</Typography>
      <Grid container spacing={2}>
        {["name", "name_tag", "description", "start_date", "end_date", "discount", "total_price", "items_number"].map((field) => (
          <Grid item xs={6} key={field}>
            <TextField
              fullWidth
              name={field}
              label={labelsMap[field] || field}
              value={offer[field]}
              onChange={handleChange}
              InputProps={ field === "items_number" ? { readOnly: true } : undefined }
              size="small"
              error={!!errors[field]}
              helperText={
                errors[field]
                  ? errors[field]
                  : field === "total_price"
                  ? "السعر الاجمالي هو السعر الاساسي للعرض وقابل للتعديل"
                  : field === "name_tag"
                  ? "التاج هو وصف مشترك لمجموعة عروض مثل (عرض نهاية العام)"
                  : ""
              }
              type={
                ["discount", "total_price", "items_number"].includes(field)
                  ? "number"
                  : ["start_date", "end_date"].includes(field)
                  ? "date"
                  : "text"
              }
              InputLabelProps={
                ["start_date", "end_date"].includes(field)
                  ? { shrink: true }
                  : undefined
              }
            />
           {/* items_number يعرض تلقائياً عدد العناصر المضافة */}
          </Grid>
        ))}

        <Grid item xs={12}>
          <Typography variant="subtitle1">المنتجات</Typography>
        </Grid>

        {offer.items.map((item, index) => (
          <React.Fragment key={index}>
            {/* Category select */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                label="المجموعة"
                value={item.category}
                onChange={(e) => handleItemChange(index, "category", e.target.value)}
                size="small"
               error={!!(errors.items && errors.items[index] && errors.items[index].category)}
               helperText={(errors.items && errors.items[index] && errors.items[index].category) || ""}
              >
                <MenuItem value="">اختر المجموعة</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Product select filtered by chosen category */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                label="المنتج"
                value={item.product_id}
                onChange={(e) => handleItemChange(index, "product_id", e.target.value)}
                size="small"
               error={!!(errors.items && errors.items[index] && errors.items[index].product_id)}
               helperText={(errors.items && errors.items[index] && errors.items[index].product_id) || ""}
              >
                <MenuItem value="">اختر المنتج</MenuItem>
                {(
                  Array.isArray(products) ? products :
                  Array.isArray(products?.data) ? products.data : []
                )
                  .filter((product) => (item.category ? product.category_name === item.category : true))
                  .map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.category_name ? `${product.category_name} - ${product.name}` : product.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={2}>
             <TextField
               fullWidth
               label="السعر"
               value={item.price}
               onChange={(e) => handleItemChange(index, "price", e.target.value)}
               type="number"
               size="small"
              error={!!(errors.items && errors.items[index] && errors.items[index].price)}
              helperText={(errors.items && errors.items[index] && errors.items[index].price) || ""}
             />
            </Grid>
            <Grid item xs={1}>
               <TextField
                 fullWidth
                 label="الكمية"
                 value={item.quantity}
                 onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                 type="number"
                 size="small"
                error={!!(errors.items && errors.items[index] && errors.items[index].quantity)}
                helperText={(errors.items && errors.items[index] && errors.items[index].quantity) || ""}
               />
             </Grid>
            <Grid item xs={2}>
               <TextField
                 fullWidth
                 label="ملاحظة"
                 value={item.note}
                 onChange={(e) => handleItemChange(index, "note", e.target.value)}
                 size="small"
               />
             </Grid>
            <Grid item xs={1} display="flex" alignItems="center" justifyContent="center">
              <Tooltip title="حذف المنتج">
                <IconButton color="error" size="small" onClick={() => removeItem(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
           </React.Fragment>
         ))}

        <Grid item xs={12}>
          <Button variant="outlined" onClick={addItem}>إضافة منتج</Button>
        </Grid>

        <Grid item xs={12}>
          <Button fullWidth variant="contained" onClick={handleSubmit}>
            إنشاء العرض
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CreateOfferForm;
